import { createEntityComponent } from "@coltorapps/builder-react";
import { useMemo, useState } from "react";
import { Label } from "@/components/ui/label";
import { RadioGroup } from "@/components/ui/radio-group";
import { formatError, ValidationError } from "@/components/ui/validation-error";
import { cn } from "@/utils/cn";
import { useRefWithErrorFocus } from "@/utils/error-focus";
import { radioButtonEntity } from "./definition";
import { OptionType } from "@/types/domain";
import { useFieldValidationState } from "@/hooks/useFieldValidationState";

export const RadioButtonEntity = createEntityComponent(
  radioButtonEntity,
  function RadioButtonEntity(props) {
    const radioGroupRef = useRefWithErrorFocus<HTMLDivElement>(
      props.entity.error,
    );

    const { localError, isValidating, validateAndCommit } =
      useFieldValidationState(props.entity.id);

    // console.debug("error", props.entity.error);

    const errorMessage =
      localError ||
      formatError(props.entity.value, props.entity.error)?._errors?.[0];

    const options = props.entity.attributes.options;

    const userValue =
      typeof props.entity.value === "string"
        ? props.entity.value
        : (props.entity.value as OptionType | undefined)?.value;
    const defaultValueAttr = props.entity.attributes.defaultValue;
    const defaultValue = useMemo(() => {
      if (typeof defaultValueAttr === "string") {
        return defaultValueAttr;
      } else if (!!defaultValueAttr && "value" in defaultValueAttr) {
        if (typeof defaultValueAttr.value === "string") {
          return defaultValueAttr.value;
        } else {
          return JSON.stringify(defaultValueAttr.value);
        }
      } else {
        return undefined;
      }
    }, [defaultValueAttr]);

    const effectiveValue = userValue ?? defaultValue ?? "";
    const isReadonly = Boolean(props.entity.attributes.readonly);
    const isDisabled = Boolean(props.entity.attributes.disabled);
    const missingSelectedValues = useMemo(() => {
      if (!effectiveValue) return [];
      const optionValues = new Set(options.map((option) => option.value));
      return optionValues.has(effectiveValue) ? [] : [effectiveValue];
    }, [effectiveValue, options]);
    const hasMissingSelectedValues = missingSelectedValues.length > 0;
    const displayOptions = useMemo(() => {
      if (!hasMissingSelectedValues) return options;

      const existingValues = new Set(options.map((option) => option.value));
      const missingOptions = missingSelectedValues
        .filter((value) => !existingValues.has(value))
        .map((value) => ({
          label: value,
          value,
          key: value,
        }));

      return [...missingOptions, ...options];
    }, [hasMissingSelectedValues, missingSelectedValues, options]);
    const missingSelectedValueMessage = hasMissingSelectedValues
      ? `Original label not found. Displaying stored value instead.`
      : undefined;
    const direction =
      (props.entity.attributes.groupDirection as "horizontal" | "vertical") ??
      "vertical";

    console.debug({ defaultValueAttr, defaultValue });

    const handleValidation = async (val?: string) => {
      if (props.entity.attributes.readonly) {
        return;
      }
      await validateAndCommit({
        value: val,
        setValue: (next) => props.setValue(next || undefined),
        validator: props.entity.attributes.validator,
        isRequiredInvalid: (value) =>
          Boolean(props.entity.attributes.required && !value),
        requiredMessage: "This field is required",
        onValidationSuccess: props.resetError,
      });
    };

    return (
      <div>
        <Label
          aria-required={props.entity.attributes.required}
          className="mb-2 block"
        >
          {!!props.entity.attributes.label.value
            ? props.entity.attributes.label.value
            : props.entity.attributes.name}
        </Label>

        <div
          className={isReadonly ? "pointer-events-none select-none" : undefined}
        >
          {displayOptions.length > 0 ? (
            <RadioGroup
              ref={radioGroupRef}
              name={props.entity.id}
              value={effectiveValue}
              onChange={(val) => {
                void handleValidation(val);
              }}
              options={displayOptions}
              className={cn(
                "flex gap-4",
                direction === "horizontal" ? "flex-row flex-wrap" : "flex-col",
              )}
              disabled={isDisabled}
            />
          ) : (
            <div ref={radioGroupRef}>
              <p className="text-sm text-muted-foreground">
                No options available
              </p>
            </div>
          )}
        </div>
        {isValidating ? (
          <p className="text-sm mt-1 text-secondary-text">
            Running validation...
          </p>
        ) : errorMessage ? (
          <ValidationError>{errorMessage}</ValidationError>
        ) : null}
        {missingSelectedValueMessage && (
          <p className="text-xs text-amber-600 mt-1">
            {missingSelectedValueMessage}
          </p>
        )}
      </div>
    );
  },
);
